import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { Tooltip } from '@abyss/web/ui/Tooltip';

import { Address as ProviderAddress } from '../../models/ProviderDetails';
import { shortenZipCode } from '../../utils/providerDetails.utils';
import { tabletScreen } from '../ConstantsStyles';
import {
  AddressText,
  AddressWrapper,
  Dot,
  GroupedPinAddressText,
  additionalAddressTextStyles,
  addressListViewTextToolTipStyles,
  addressTextToolTipStyles,
  popupAddressTextToolTipStyles,
} from './DataCard.style';

type Props = {
  address?: ProviderAddress;
  expandedProviderListView?: boolean;
  id?: string;
  popupContainer?: boolean;
  sectionType?: string;
  isCTADisplayLogic?: boolean;
  showSmartChoice?: boolean;
  badgeCardType?: string;
  enableMapViewHideOption?: boolean;
  isGroupedLocationDrawer?: boolean;
};
const maxWidthWithnewDesign = (
  expandedProviderListView,
  enableMapViewHideOption
) =>
  !expandedProviderListView && enableMapViewHideOption
    ? {
        maxWidth: '180px !important',
        '@media (min-width: 360px) and (max-width: 400px)': {
          maxWidth: '130px !important',
        },
      }
    : {};
const maxWidthAddress = (
  showSmartChoice,
  maxWidthWithBadgeType,
  expandedProviderListView,
  enableMapViewHideOption
) =>
  showSmartChoice
    ? {
        '&.abyss-text-root': { maxWidth: '180px' },
        ...maxWidthWithBadgeType,
        ...maxWidthWithnewDesign(
          expandedProviderListView,
          enableMapViewHideOption
        ),
      }
    : {};
const tooltipStyles = (enableMapViewHideOption, expandedProviderListView) =>
  enableMapViewHideOption
    ? addressListViewTextToolTipStyles(expandedProviderListView)
    : addressTextToolTipStyles;

export const Address = ({
  address,
  expandedProviderListView = false,
  enableMapViewHideOption = false,
  id,
  popupContainer,
  sectionType,
  isCTADisplayLogic = false,
  showSmartChoice = false,
  badgeCardType = 'data-card',
  isGroupedLocationDrawer = false,
}: Props) => {
  const TabletScreen = useMediaQuery(tabletScreen);
  const { line } = address || {};
  const addressLine = line?.join(', ');
  const addressFullText = `${addressLine} ${address?.city ?? ''}, ${
    address?.state ?? ''
  } ${shortenZipCode(address?.postalCode) ?? ''}`;
  const maxWidthWithBadgeType =
    badgeCardType === 'data-card' ? { maxWidth: '220px !important' } : {};

  if (!line) return null;

  const renderFullTextAddress = () => {
    const shortenZip = shortenZipCode(address?.postalCode);
    let fullText =
      address && address.city && address.state
        ? `${address.city}, ${address.state}`
        : '';

    if (fullText.length > 60) {
      fullText = `${fullText.slice(0, 60)}...`;
    }

    return (
      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <span style={{ whiteSpace: 'nowrap' }}>{addressLine}</span>
        <br />
        <span style={{ whiteSpace: 'nowrap' }}>
          {fullText} {shortenZip}
        </span>
      </div>
    );
  };

  return (
    <AddressWrapper>
      {!isCTADisplayLogic && !popupContainer && !isGroupedLocationDrawer && (
        <Dot />
      )}
      {isGroupedLocationDrawer ? (
        <GroupedPinAddressText
          color={'$gray8'}
          data-auto-testid={`data-card-address-${sectionType}-${id}`}
          data-testid={`data-card-address-${sectionType}-${id}`}
        >
          <Flex>{`${addressLine}`}</Flex>
          <Flex>
            {`${address?.city ?? ''}, ${address?.state ?? ''} ${
              shortenZipCode(address?.postalCode) ?? ''
            }`}
          </Flex>
        </GroupedPinAddressText>
      ) : (
        <Tooltip
          content={enableMapViewHideOption ? addressFullText : addressLine}
          css={
            popupContainer
              ? popupAddressTextToolTipStyles
              : tooltipStyles(enableMapViewHideOption, expandedProviderListView)
          }
          position="top"
          positionOffset={8}
        >
          <AddressText
            color={
              popupContainer
                ? '$gray7'
                : isCTADisplayLogic
                ? '$interactive1'
                : '$gray7'
            }
            css={{
              marginTop: '0px !important',
              fontSize: popupContainer && '$sm',
              fontWeight: expandedProviderListView
                ? '100 !important'
                : '100 !important',
              lineHeight: expandedProviderListView
                ? '20px !important'
                : '15px !important',
              padding: popupContainer && '0px 10px',
              height: enableMapViewHideOption && '50px',
              ...additionalAddressTextStyles(
                expandedProviderListView,
                enableMapViewHideOption,
                TabletScreen,
                popupContainer
              ),
              '@media (min-width: 360px) and (max-width: 400px)': {
                maxWidth: '130px !important',
              },
              ...maxWidthAddress(
                showSmartChoice,
                maxWidthWithBadgeType,
                expandedProviderListView,
                enableMapViewHideOption
              ),
            }}
            cssProps={{
              enableMapViewHideOption,
            }}
            data-auto-testid={`data-card-address-${sectionType}-${id}`}
            data-testid={`data-card-address-${sectionType}-${id}`}
            fontWeight={
              popupContainer ? '$small' : isCTADisplayLogic ? '$bold' : '$small'
            }
          >
            {enableMapViewHideOption ? renderFullTextAddress() : addressLine}
          </AddressText>
        </Tooltip>
      )}
    </AddressWrapper>
  );
};
